/**
 * ONE way to shorten a long machine identifier for display (flowstack#1223 F).
 *
 * The app had three renderings of a content hash — `sha256:abcd…wxyz` in the
 * scan-identity line, a bare `slice(0, 24)…` on the artifact card, a
 * `slice(0, 12)…` and a `slice(0, 16)…` in the submission pickers — plus one
 * surface (the molecule panel's payload name) that did not shorten at all and
 * simply ran off the panel edge, cut mid-string with no ellipsis. A reader who
 * has learned that an identifier ends in `…` reads an un-elided cut as the
 * whole value.
 *
 * Both functions here are pure and total: whatever they are given, they return
 * something a `title` attribute can carry the full value behind. Neither ever
 * returns a string LONGER than its input, so nothing "shortened" grows.
 */

/**
 * `sha256:abcd1234…wxyz` — enough to tell two artifacts apart at a glance.
 *
 * Keeps the algorithm prefix, because that prefix is what makes the rest of the
 * string legible AS a hash rather than as an opaque hex run. It never
 * MANUFACTURES one: a value that arrives without `algo:` — a fixture id, a
 * producer-assigned key — falls through to {@link truncateMiddle} rather than
 * being dressed up as something it is not. What it will not do is hand back an
 * unbounded string, which is the failure the un-elided payload name was.
 *
 * The full value belongs on the element's `title`, and is what a comparison
 * uses — the elided form is for the eye.
 */
export function shortHash(contentHash: string): string {
  const [algo, hex] = contentHash.split(":");
  if (hex === undefined) return truncateMiddle(contentHash);
  if (hex.length <= 16) return contentHash;
  return `${algo}:${hex.slice(0, 8)}…${hex.slice(-4)}`;
}

/**
 * Any long mono string, elided in the MIDDLE — `8401ed6e…a867163.sdf`.
 *
 * For identifiers that are not `algo:hex` and so have no prefix to keep: a
 * payload name, a producer-assigned id. Middle elision rather than a trailing
 * cut because the discriminating characters of a hash-named file are at both
 * ends, and because the tail is where a file extension lives — the one part of
 * such a name that tells a reader what the thing IS.
 *
 * `max` is the budget for the RESULT including the ellipsis, so the caller can
 * reason about the width it is fitting into.
 */
export function truncateMiddle(value: string, max = 28): string {
  if (value.length <= max) return value;
  const keep = max - 1;
  const head = Math.ceil(keep / 2);
  const tail = keep - head;
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
}
